import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

const DURATION_MS = 800;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

interface CountUpNumberProps {
  value: number;
  className?: string;
  'data-test-id'?: string;
}

/**
 * A points figure that ticks up to its value the first time it scrolls into view, and
 * animates between values whenever the score changes afterwards.
 *
 * Starts at zero ONLY when a real count-up is going to happen. Whenever it cannot animate
 * — reduced motion, or an environment with no IntersectionObserver, which includes the
 * jsdom test runner — the very first render is the true figure, so no caller ever reads a
 * placeholder zero off the DOM.
 */
export const CountUpNumber: React.FC<CountUpNumberProps> = ({
  value,
  className,
  'data-test-id': dataTestId,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const canAnimate = typeof IntersectionObserver !== 'undefined' && !prefersReducedMotion;

  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(() => (canAnimate ? 0 : value));
  // The rAF loop reads the current figure without re-subscribing the effect to it, which
  // would restart the animation on its own output.
  const displayRef = useRef(display);
  const frameRef = useRef<number | null>(null);
  const [isInView, setIsInView] = useState(!canAnimate);

  useEffect(() => {
    if (isInView || !ref.current) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      // Positive margin, so the observed area is GROWN and the tick-up starts just before
      // the row reaches the screen. A negative bottom margin does the opposite: it shrinks
      // the area, and a row sitting in the bottom band of the screen would be plainly
      // visible while still reading 0 — a wrong number, not merely a missed animation.
      { rootMargin: '0px 0px 10% 0px' }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isInView]);

  useEffect(() => {
    if (!isInView) return;

    const from = displayRef.current;
    if (from === value) return;

    const start = performance.now();

    const step = (now: number) => {
      const progress = Math.min((now - start) / DURATION_MS, 1);
      const next = Math.round(from + (value - from) * easeOutCubic(progress));
      displayRef.current = next;
      setDisplay(next);
      if (progress < 1) frameRef.current = requestAnimationFrame(step);
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [isInView, value]);

  return (
    // `tabular-nums` keeps every digit the same width, so a figure ticking from 0 to
    // 1,284 does not jiggle the column it sits in while it counts.
    <span ref={ref} className={`tabular-nums ${className ?? ''}`} data-test-id={dataTestId}>
      {display}
    </span>
  );
};

export default CountUpNumber;
